<template>
  <v-dialog v-model="dialog" width="90%" scrollable>
    <template v-slot:activator="{ props }">
      <v-btn v-bind="props" color="primary" variant="outlined">
        <template v-if="!linkedVehicle">
          Voertuig koppelen
        </template>
        <template v-else>
          Voertuig wijzigen
        </template>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center">
        <v-btn
          v-if="step === 'vehicles'"
          icon="mdi-arrow-left"
          variant="text"
          density="comfortable"
          class="mr-2"
          @click="step = 'types'"
        />
        <span v-if="step === 'types'">Kies een voertuigtype</span>
        <span v-else>{{ selectedType.brand }} {{ selectedType.model }} ({{ typeNameLabel(selectedType.name) }})</span>
      </v-card-title>

      <v-card-text>
        <template v-if="step === 'types'">
          <template v-if="loadingTypes">
            <div class="text-center py-8">
              <v-progress-circular indeterminate />
            </div>
          </template>
          <v-row v-else>
            <v-col
              v-for="type in vehicleTypes"
              :key="type.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-card
                class="vehicle-type-card"
                hover
                @click="selectType(type)"
              >
                <div class="vehicle-type-image">
                  <img :src="type.image_url" />
                </div>
                <v-card-text class="text-center pb-0">
                  <span class="font-weight-bold">{{ type.brand }}</span>
                  {{ type.model }}
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <div v-if="loadingVehicles" class="text-center py-8">
            <v-progress-circular indeterminate />
          </div>
          <div v-else-if="!vehiclesOfSelectedType.length" class="text-center py-8">
            Geen voertuigen gevonden voor dit type.
          </div>
          <v-radio-group v-else v-model="selectedVehicleId" hide-details>
            <v-table density="compact">
              <thead>
                <tr>
                  <th class="radio-column"></th>
                  <th>Naam</th>
                  <th>Kenteken</th>
                  <th>Vestiging</th>
                  <th>Jaar</th>
                  <th>Brandstoftype</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicle in vehiclesOfSelectedType"
                  :key="vehicle.id"
                  class="vehicle-row"
                  @click="selectedVehicleId = vehicle.id"
                >
                  <td class="radio-column"><v-radio :value="vehicle.id" /></td>
                  <td><span class="vehicle-name-badge">{{ vehicle.name }}</span></td>
                  <td>
                    <span class="nl-license-plate">
                      <span class="nl-license-plate-eu">NL</span>
                      <span class="nl-license-plate-number">{{ vehicle.license_plate }}</span>
                    </span>
                  </td>
                  <td>{{ vehicle.branch?.name }}</td>
                  <td>{{ vehicle.year }}</td>
                  <td>{{ fuelTypeLabel(vehicle.fuel_type) }}</td>
                  <td>{{ statusLabel(vehicle.status) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-radio-group>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="dialog = false">
          Annuleren
        </v-btn>
        <v-btn
          v-if="step === 'vehicles'"
          color="primary"
          :disabled="!selectedVehicleId"
          @click="save"
        >
          Opslaan
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import textMixin from '@/mixins/text'

export default {
  mixins: [textMixin],
  props: {
    linkedVehicle: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialog: false,
      step: 'types',
      loadingTypes: false,
      loadingVehicles: false,
      selectedType: null,
      selectedVehicleId: null,
    }
  },

  computed: {
    vehicleTypes() {
      return this.$store.getters.vehiclesTypesList
    },
    vehicles() {
      return this.$store.getters.vehiclesAllList
    },
    vehiclesOfSelectedType() {
      if (!this.selectedType) {
        return []
      }
      return this.vehicles.filter(vehicle => vehicle.vehicle_type_id === this.selectedType.id)
    },
  },

  watch: {
    dialog(value) {
      if (value) {
        this.open()
      }
    },
  },

  methods: {
    fuelTypeLabel(fuelType) {
      if (!fuelType) {
        return fuelType
      }
      return this.text.vehicles.fuelTypes[fuelType.toLowerCase()] ?? fuelType
    },

    statusLabel(status) {
      if (!status) {
        return status
      }
      return this.text.vehicles.statuses[status] ?? status
    },

    typeNameLabel(typeName) {
      if (!typeName) {
        return typeName
      }
      return this.text.vehicles.typeNames[typeName] ?? typeName
    },

    open() {
      this.step = 'types'
      this.selectedType = null
      this.selectedVehicleId = this.linkedVehicle?.id ?? null

      this.loadingTypes = true
      this.$store.dispatch('vehicles.fetchTypes').finally(() => {
        this.loadingTypes = false
      })

      this.loadingVehicles = true
      this.$store.dispatch('vehicles.fetchAll').finally(() => {
        this.loadingVehicles = false
      })
    },

    selectType(type) {
      this.selectedType = type
      this.step = 'vehicles'
    },

    save() {
      const vehicle = this.vehicles.find(v => v.id === this.selectedVehicleId)
      if (!vehicle) {
        return
      }
      this.$store.commit('app.setVehicle', vehicle)
      this.dialog = false
    },
  },
}
</script>

<style scoped>
.vehicle-type-card {
  cursor: pointer;
  padding: 16px;
}
.vehicle-type-image {
  aspect-ratio: 3 / 2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.vehicle-type-image img {
  display: block;
  max-height: 100%;
  max-width: 100%;
}
.vehicle-row {
  cursor: pointer;
}
.radio-column {
  max-width: 50px;
}
</style>
